import {gql} from '@apollo/client';
import React from 'react';

import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {STUDY_DESCRIPTION_MAX_LENGTH} from 'studies/StudyConstants';
import {TextFieldType} from 'ui-library/inputs/EditableTextField';
import SettingsField from 'ui-library/SettingsField';

import * as StudyDAO from '../StudyDAO';
import {StudyDescriptionQuery, StudyDescriptionQueryVariables} from './__generated/StudyDescriptionQuery';
import {StudyOverviewViewQuery} from './overview/__generated/StudyOverviewViewQuery';

type StudyNode = ExtractByTypename<StudyOverviewViewQuery['study'], 'StudyNode'>;

export interface StudyDescriptionEditorProps {
  study: Pick<StudyNode, 'id' | 'description'>;
  refetch: () => Promise<unknown>;
  isWritable: boolean;
}

export const STUDY_DESCRIPTION_PLACEHOLDER = 'Add study description';

export const STUDY_DESCRIPTION_QUERY = gql`
  query StudyDescriptionQuery($id: StrictString!) @skipAudit @engComponent(name: STUDIES) {
    study(id: $id) {
      ... on StudyNode {
        id
        description
      }
    }
  }
`;

export default function StudyDescriptionEditor({
  study,
  refetch,
  isWritable,
}: StudyDescriptionEditorProps): JSX.Element | null {
  const setDescription = async (description: string): Promise<void> => {
    await StudyDAO.updateStudy(study.id, {description});
    await refetch();
  };

  return (
    <SettingsField
      isBeingCreated={false}
      isWritable={isWritable}
      label=""
      name="description"
      onChange={(updatedDescription) => {
        const description: string = updatedDescription as string;
        setDescription(description);
      }}
      value={study.description}
      emptyValueAllowed={true}
      textPlaceholder={STUDY_DESCRIPTION_PLACEHOLDER}
      type={TextFieldType.TEXTAREA}
      maxLength={STUDY_DESCRIPTION_MAX_LENGTH}
      autoSize={true}
    />
  );
}

export interface QueryingStudyDescriptionEditorProps {
  studyId: string;
  isWritable: boolean;
}

export function QueryingStudyDescriptionEditor({
  studyId,
  isWritable,
}: QueryingStudyDescriptionEditorProps): JSX.Element | null {
  const result = useBenchlingQuery<StudyDescriptionQuery, StudyDescriptionQueryVariables>(
    STUDY_DESCRIPTION_QUERY,
    {
      variables: {id: studyId},
    }
  );
  if (result.type !== QueryResultType.SUCCESS || isInaccessible(result.data.study)) {
    return null;
  }

  return (
    <StudyDescriptionEditor study={result.data.study} isWritable={isWritable} refetch={result.refetch} />
  );
}
